import {
  PracticeTemplate,
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
} from '@cibo/core'
import { useAuth } from '@cibo/profile'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { map, pipe, prop, uniq } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import {
  useCreatePracticeTemplate,
  useDeletePracticeTemplate,
  usePracticeTemplates,
  useUpdatePracticeTemplate,
} from '../../../../../queries'
import { FieldTraitTemplateEditorMain } from './FieldTraitTemplateEditorMain'
import { TemplateEditorContext } from './TemplateEditorContext'
import { TemplateEditorSidebar } from './TemplateEditorSidebar'
import { TemplateUnsavedGuard } from './TemplateUnsavedGuard'

interface FieldTraitTemplateEditorModalProps {
  open: boolean
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
  initialTemplateId?: string
}

// disabled at 19
// eslint-disable-next-line visual/complexity
export const FieldTraitTemplateEditorModal = ({
  open,
  onClose,
  task,
  initialTemplateId,
}: FieldTraitTemplateEditorModalProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { enqueueSnackbar } = useSnackbar()
  const { userId: authUserId, persona } = useAuth()
  const { engagement, program, fieldModels, userId: farmerUserId } = useProgramEngagementContext()

  const programId = engagement.data?.programId ?? ''
  const taskId = task.id
  const benchmark = program.data?.benchmarks?.find(
    b => b.benchmark === task.rule.params?.benchmarkName
  )

  // Unique trait IDs — no year dimension
  const traitIds: string[] = useMemo(
    () => pipe(map(prop('traitId')), uniq)(benchmark?.details ?? []) as string[],
    [benchmark?.details]
  )

  const [selectedId, setSelectedId] = useState<string | 'new'>(initialTemplateId ?? 'new')

  useEffect(() => {
    if (open) setSelectedId(initialTemplateId ?? 'new')
  }, [open, initialTemplateId])

  const [draftName, setDraftName] = useState('')
  const [draftDetails, setDraftDetails] = useState<PracticeTemplateDetail[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [ownerScope, setOwnerScope] = useState<'farmer' | 'all'>('farmer')

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const templateOwner = ownerScope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!
  const [nameError, setNameError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showUnsavedGuard, setShowUnsavedGuard] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | 'new' | null>(null)
  const [shouldFocusName, setShouldFocusName] = useState(false)

  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const draftNameRef = useRef(draftName)
  draftNameRef.current = draftName
  const draftDetailsRef = useRef(draftDetails)
  draftDetailsRef.current = draftDetails
  const templateOwnerRef = useRef(templateOwner)
  templateOwnerRef.current = templateOwner
  const isCreatingRef = useRef(false)

  const isAdvisorOrAdmin = persona === 'advisor' || persona === 'admin'
  const ownTemplates = usePracticeTemplates(
    { programId, taskId, owner: authUserId },
    { enabled: open && !!programId && !isAdvisorOrAdmin }
  )
  const allTemplates = usePracticeTemplates(
    { programId, taskId },
    { enabled: open && !!programId && isAdvisorOrAdmin }
  )

  const createTemplate = useCreatePracticeTemplate()
  const updateTemplate = useUpdatePracticeTemplate()
  const deleteTemplate = useDeletePracticeTemplate()

  const sidebarTemplates: PracticeTemplate[] = useMemo(() => {
    if (isAdvisorOrAdmin && allTemplates.data) return allTemplates.data
    return ownTemplates.data ?? []
  }, [isAdvisorOrAdmin, allTemplates.data, ownTemplates.data])

  const isLoadingTemplates = isAdvisorOrAdmin ? allTemplates.isLoading : ownTemplates.isLoading

  const selectedTemplate = sidebarTemplates.find(tmpl => tmpl.id === selectedId)
  const isOwnTemplate = selectedId === 'new' || selectedTemplate?.owner === authUserId
  const canEdit = isOwnTemplate || isAdvisorOrAdmin
  const canDelete =
    selectedId !== 'new' && (isOwnTemplate || persona === 'advisor' || persona === 'admin')

  const availableTraitIds: string[] = useMemo(() => {
    const existing = new Set(draftDetails.map(d => d.traitId))
    return traitIds.filter(id => !existing.has(id))
  }, [traitIds, draftDetails])

  useEffect(() => {
    if (!open) return
    if (selectedId === 'new') {
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      setNameError(null)
    } else if (selectedTemplate) {
      setDraftName(selectedTemplate.name)
      setDraftDetails(selectedTemplate.details)
      setIsDirty(false)
      setNameError(null)
      setOwnerScope(selectedTemplate.owner === farmerUserId ? 'farmer' : 'all')
    }
  }, [selectedId, selectedTemplate?.id, open])

  const requestNavigate = (targetId: string | 'new') => {
    if (isDirty) {
      setPendingNavigation(targetId)
      setShowUnsavedGuard(true)
    } else {
      setSelectedId(targetId)
    }
  }

  const requestClose = () => {
    if (isDirty) {
      setPendingNavigation(null)
      setShowUnsavedGuard(true)
    } else {
      onClose()
    }
  }

  const handleUnsavedDiscard = () => {
    setShowUnsavedGuard(false)
    setIsDirty(false)
    if (pendingNavigation !== null) {
      setSelectedId(pendingNavigation)
      setPendingNavigation(null)
    } else {
      onClose()
    }
  }

  const handleValueSave = useCallback(
    (
      traitId: string,
      year: number,
      filledValues: { value?: unknown; input?: unknown; result?: unknown }
    ) => {
      const value = filledValues?.value !== undefined ? { value: filledValues.value } : {}
      const input = filledValues?.input !== undefined ? { input: filledValues.input } : {}
      const result = filledValues?.result !== undefined ? { result: filledValues.result } : {}
      const prev = draftDetailsRef.current
      const idx = prev.findIndex(d => d.traitId === traitId && d.year === year)
      const newDetails =
        idx >= 0
          ? prev.map((d, i) => (i === idx ? { ...d, ...value, ...input, ...result } : d))
          : [...prev, { traitId, year, ...value, ...input, ...result }]
      setDraftDetails(newDetails)
      setIsDirty(true)
    },
    []
  )

  const handleValueDelete = useCallback((traitId: string, year: number) => {
    const newDetails = draftDetailsRef.current.filter(
      d => !(d.traitId === traitId && d.year === year)
    )
    setDraftDetails(newDetails)
    setIsDirty(true)
  }, [])

  // For non-yearly data: add a single entry with year=0
  const handleAddPractice = useCallback((traitId: string) => {
    const newDetails = [...draftDetailsRef.current, { traitId, year: 0 }]
    setDraftDetails(newDetails)
    setIsDirty(true)
  }, [])

  const handlePrePopulate = useCallback(
    (fieldId: string) => {
      const field = fieldModels.data?.items?.find(f => f.id === fieldId)
      if (!field || !benchmark) return
      const newDetails: PracticeTemplateDetail[] = traitIds
        .map(traitId => {
          const fieldDetail = field.findDetail({ traitId })
          if (!fieldDetail) return null
          const value = fieldDetail.value !== undefined ? { value: fieldDetail.value } : {}
          const input = fieldDetail.input !== undefined ? { input: fieldDetail.input } : {}
          const result = fieldDetail.result !== undefined ? { result: fieldDetail.result } : {}
          if (
            !Object.keys(value).length &&
            !Object.keys(input).length &&
            !Object.keys(result).length
          )
            return null
          return { traitId, year: 0, ...input, ...value, ...result }
        })
        .filter(Boolean) as PracticeTemplateDetail[]
      setDraftDetails(newDetails)
      setIsDirty(true)
    },
    [fieldModels.data?.items, benchmark, traitIds]
  )

  const onConfirmName = useCallback((): boolean => {
    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setNameError(t('nameRequiredError'))
      return false
    }
    const isDuplicate = sidebarTemplates.some(
      tmpl => tmpl.name === trimmedName && (selectedId === 'new' || tmpl.id !== selectedId)
    )
    if (isDuplicate) {
      setNameError(t('duplicateNameError'))
      return false
    }
    setNameError(null)
    setIsDirty(true)
    return true
  }, [draftName, sidebarTemplates, selectedId, t])

  const handleSave = async (): Promise<boolean> => {
    if (!onConfirmName()) return false
    const trimmedName = draftName.trim()

    try {
      if (selectedId === 'new') {
        if (isCreatingRef.current) return false
        isCreatingRef.current = true
        try {
          const created = await createTemplate.mutateAsync({
            owner: templateOwner,
            name: trimmedName,
            programId,
            taskId,
            details: draftDetails,
          })
          setIsDirty(false)
          setSelectedId(created.item.id)
        } finally {
          isCreatingRef.current = false
        }
      } else {
        await updateTemplate.mutateAsync({
          id: selectedId,
          programId,
          taskId,
          owner: templateOwner,
          name: trimmedName,
          details: draftDetails,
        })
        setIsDirty(false)
      }
      enqueueSnackbar(t('saveSuccess'), { variant: 'success' })
      return true
    } catch {
      enqueueSnackbar(t('saveError'), { variant: 'error' })
      return false
    }
  }

  const handleSaveAndClose = async () => {
    const success = await handleSave()
    if (success) {
      setShowUnsavedGuard(false)
      onClose()
    }
  }

  const handleOwnerScopeChange = useCallback(
    async (scope: 'farmer' | 'all') => {
      setOwnerScope(scope)
      if (selectedIdRef.current === 'new') return
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newOwner = scope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!
      try {
        await updateTemplate.mutateAsync({
          id: selectedIdRef.current,
          programId,
          taskId,
          owner: newOwner,
          name: draftNameRef.current.trim(),
          details: draftDetailsRef.current,
        })
      } catch {
        enqueueSnackbar(t('saveError'), { variant: 'error' })
      }
    },
    [farmerUserId, authUserId, updateTemplate, programId, taskId, enqueueSnackbar, t]
  )

  const handleDeleteConfirm = async () => {
    if (selectedId === 'new') return
    try {
      await deleteTemplate.mutateAsync(selectedId)
      setShowDeleteConfirm(false)
      setSelectedId('new')
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      enqueueSnackbar(t('deleteSuccess'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('deleteError'), { variant: 'error' })
    }
  }

  const isSaving = createTemplate.isPending || updateTemplate.isPending
  const hasPractices = draftDetails.length > 0
  const hasName = !!draftName.trim()
  const canSave = isDirty && hasPractices && !isSaving && hasName

  const handleNameAndSave = useCallback(() => {
    setShowUnsavedGuard(false)
    setShouldFocusName(true)
  }, [])

  return (
    <TemplateEditorContext.Provider
      value={{ canSave, isSaving, hasName, onNameAndSave: handleNameAndSave }}
    >
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="lg"
        fullWidth
        data-testid="field-trait-template-editor-modal"
      >
        <Stack direction="row" sx={{ height: '80vh', overflow: 'hidden' }}>
          <TemplateEditorSidebar
            templates={sidebarTemplates}
            isLoading={isLoadingTemplates}
            selectedId={selectedId}
            onSelect={requestNavigate}
            isAdvisorOrAdmin={isAdvisorOrAdmin}
            farmerUserId={farmerUserId}
            authUserId={authUserId}
          />
          <FieldTraitTemplateEditorMain
            task={task}
            draftDetails={draftDetails}
            onValueSave={handleValueSave}
            onValueDelete={handleValueDelete}
            onAddPractice={handleAddPractice}
            onPrePopulate={handlePrePopulate}
            availableTraitIds={availableTraitIds}
            fields={fieldModels.data?.items ?? []}
            canEdit={canEdit}
            canChangeOwnerScope={isAdvisorOrAdmin}
            canDelete={canDelete}
            isDeleting={deleteTemplate.isPending}
            isSaving={createTemplate.isPending || updateTemplate.isPending}
            isDirty={isDirty}
            draftName={draftName}
            nameError={nameError}
            selectedId={selectedId}
            onDraftNameChange={name => {
              setDraftName(name)
              setNameError(null)
            }}
            onConfirmName={onConfirmName}
            onDelete={() => setShowDeleteConfirm(true)}
            onClose={requestClose}
            onSave={handleSave}
            ownerScope={ownerScope}
            onOwnerScopeChange={handleOwnerScopeChange}
            shouldFocusName={shouldFocusName}
            onShouldFocusNameHandled={() => setShouldFocusName(false)}
          />
        </Stack>
      </Dialog>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>{t('deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteConfirmMessage', { name: selectedTemplate?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            loading={deleteTemplate.isPending}
            onClick={handleDeleteConfirm}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateUnsavedGuard
        open={showUnsavedGuard}
        selectedId={selectedId}
        onKeepEditing={() => setShowUnsavedGuard(false)}
        onDiscard={handleUnsavedDiscard}
        onSaveAndClose={handleSaveAndClose}
      />
    </TemplateEditorContext.Provider>
  )
}
